import api from './apiClient';
import { getApiTimeouts, isMobileDevice } from '../utils/apiTimeouts';
import {
  Pet,
  PetDetail,
  PetPublicInfo,
  PublicPetTokenResponse,
  PetQrSessionResponse,
  PetInsightSummary,
  PublicMedicalControlPayload,
  PublicMedicalControlUpdatePayload,
  MedicalControl,
  LatestInterventions,
  PetQrSettings,
  AIUsageInfo,
} from '../types';

export const petService = {
  getMyPets: async (): Promise<Pet[]> => {
    const response = await api.get('/pets/');
    return response.data;
  },

  getPet: async (id: number): Promise<PetDetail> => {
    const response = await api.get(`/pets/${id}`);
    return response.data;
  },

  getPublicPetToken: async (id: number): Promise<PublicPetTokenResponse> => {
    const response = await api.post(`/pets/public/${id}/token`);
    return response.data;
  },

  generateQrSession: async (id: number, qrType: 'personal' | 'veterinarian' = 'personal'): Promise<PetQrSessionResponse> => {
    const response = await api.post(`/pets/${id}/qr-session`, {
      qr_type: qrType
    });
    return response.data;
  },

  getQrSession: async (id: number): Promise<PetQrSessionResponse | null> => {
    const response = await api.get(`/pets/${id}/qr-session`);
    return response.data;
  },

  deleteQrSession: async (id: number): Promise<void> => {
    await api.delete(`/pets/${id}/qr-session`);
  },

  getPublicPet: async (id: number, token: string): Promise<PetPublicInfo> => {
    const response = await api.get(`/pets/public/${id}`, { params: { token } });
    return response.data;
  },

  getPublicPetInsightSummary: async (id: number, token: string, regenerate = false): Promise<PetInsightSummary> => {
    const response = await api.get(`/pets/public/${id}/insights/summary`, {
      params: { token, regenerate: regenerate ? 'true' : undefined },
    });
    return response.data;
  },

  getPublicAvailableYears: async (id: number, token: string): Promise<{ years: number[] }> => {
    const response = await api.get(`/pets/public/${id}/available-years`, { params: { token } });
    return response.data;
  },

  downloadPublicPetInsightSummaryPDF: async (id: number, token: string, petName: string): Promise<void> => {
    const response = await api.get(`/pets/public/${id}/insights/summary/pdf`, {
      params: { token },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resumen_clinico_${petName.replace(' ', '_')}_${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  getPublicPetAIUsage: async (id: number, token: string): Promise<AIUsageInfo> => {
    const response = await api.get(`/pets/public/${id}/ai/usage`, { params: { token } });
    return response.data;
  },

  createPublicMedicalControl: async (
    petId: number,
    token: string,
    controlData: PublicMedicalControlPayload
  ): Promise<MedicalControl> => {
    const response = await api.post(`/pets/public/${petId}/controls`, controlData, { params: { token } });
    return response.data;
  },

  updatePublicMedicalControl: async (
    petId: number,
    controlId: number,
    token: string,
    controlData: PublicMedicalControlUpdatePayload
  ): Promise<MedicalControl> => {
    const response = await api.put(
      `/pets/public/${petId}/controls/${controlId}`,
      controlData,
      { params: { token } }
    );
    return response.data;
  },

  associateControlToVeterinarian: async (
    petId: number,
    controlId: number,
    veterinarianId: number,
    token: string
  ): Promise<MedicalControl> => {
    const response = await api.patch(
      `/pets/public/${petId}/controls/${controlId}/associate-veterinarian`,
      {},
      { params: { token, veterinarian_id: veterinarianId } }
    );
    return response.data;
  },

  createPet: async (petData: Partial<Pet>, draftUuid?: string): Promise<Pet> => {
    const params = draftUuid ? { draft_uuid: draftUuid } : {};
    
    const timeouts = await getApiTimeouts();
    const timeout = isMobileDevice() 
      ? timeouts.petOperations.mobile 
      : timeouts.petOperations.desktop;
    
    try {
      const response = await api.post('/pets', petData, { 
        params,
        timeout: timeout,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('La operación tardó demasiado. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      throw error;
    }
  },

  updatePet: async (id: number, petData: Partial<Pet>): Promise<Pet> => {
    const timeouts = await getApiTimeouts();
    const timeout = isMobileDevice() 
      ? timeouts.petOperations.mobile 
      : timeouts.petOperations.desktop;
    
    try {
      const response = await api.put(`/pets/${id}`, petData, {
        timeout: timeout,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('La operación tardó demasiado. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      throw error;
    }
  },

  deletePet: async (id: number, deletionReason: string): Promise<void> => {
    await api.delete(`/pets/${id}`, { data: { deletion_reason: deletionReason } });
  },

  reactivatePet: async (id: number): Promise<void> => {
    await api.post(`/pets/${id}/reactivate`);
  },

  permanentlyDeletePet: async (id: number): Promise<void> => {
    await api.delete(`/pets/${id}/permanent`);
  },

  markPetAsDeceased: async (id: number, deceasedDate?: string): Promise<void> => {
    const url = deceasedDate 
      ? `/pets/${id}/mark-deceased?deceased_date=${deceasedDate}`
      : `/pets/${id}/mark-deceased`;
    await api.post(url);
  },

  unmarkPetAsDeceased: async (id: number): Promise<void> => {
    await api.post(`/pets/${id}/unmark-deceased`);
  },

  getAllPetsAdmin: async (
    includeInactive: boolean = false,
    page: number = 1,
    limit: number = 20,
    search: string = ''
  ): Promise<{ items: Pet[]; total: number; page: number; page_size: number; total_pages: number }> => {
    const response = await api.get('/pets/admin/all', {
      params: { 
        include_inactive: includeInactive,
        page,
        limit,
        search: search.trim()
      }
    });
    return response.data;
  },

  searchPetsByChip: async (microchip: string, clinicId?: number): Promise<Pet[]> => {
    const response = await api.get('/pets/search', {
      params: {
        microchip,
        clinic_id: clinicId
      }
    });
    return response.data;
  },

  searchPetsByOwnerRut: async (ownerRut: string, clinicId?: number): Promise<Pet[]> => {
    const response = await api.get('/pets/search', {
      params: {
        owner_rut: ownerRut,
        clinic_id: clinicId
      }
    });
    return response.data;
  },

  getPetsByClinic: async (clinicId: number): Promise<Pet[]> => {
    const response = await api.get(`/pets/clinic/${clinicId}`);
    return response.data;
  },

  getQrSettings: async (): Promise<PetQrSettings> => {
    const response = await api.get('/pets/qr-settings');
    return response.data;
  },

  getLatestInterventions: async (id: number): Promise<LatestInterventions> => {
    const response = await api.get(`/pets/${id}/interventions`);
    return response.data;
  },

  createClinicRequest: async (petId: number, clinicId: number): Promise<any> => {
    const response = await api.post(`/pets/${petId}/clinic-request`, { clinic_id: clinicId });
    return response.data;
  },

  cancelClinicRequest: async (petId: number, requestId: number, reason?: string): Promise<any> => {
    const response = await api.delete(`/pets/${petId}/clinic-request/${requestId}`, {
      data: { reason },
    });
    return response.data;
  },

  getMyClinicRequests: async (status?: string): Promise<any[]> => {
    const response = await api.get('/pets/clinic-requests/my', {
      params: status ? { status } : {},
    });
    return response.data;
  },
};



